import { Box, Divider, TextField, Typography } from '@mui/material'

type TranslationField = { name: string; description: string }

export const TranslationsTab = ({
  currentName,
  currentDescription,
  translationFields,
  translationsValidationErrors = {},
  onUpdate,
  onBlur,
  showDescription = true,
}: {
  currentName: string
  currentDescription?: string
  translationFields: Record<string, TranslationField>
  translationsValidationErrors?: Record<string, { name?: string; description?: string }>
  onUpdate: (locale: string, field: 'name' | 'description', value: string) => void
  onBlur?: (locale: string, field: 'name' | 'description') => void
  showDescription?: boolean
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
      }}
    >
      <Box sx={{ width: 28, flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          flex: 1,
          fontWeight: 600,
        }}
      >
        Name
      </Typography>
      {showDescription && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            flex: 1,
            fontWeight: 600,
          }}
        >
          Description
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
      }}
    >
      <Box sx={{ width: 28, flexShrink: 0 }} />
      <Typography variant="body2" sx={{ flex: 1 }}>
        {currentName}
      </Typography>
      {showDescription && (
        <Typography variant="body2" sx={{ flex: 1 }}>
          {currentDescription || '—'}
        </Typography>
      )}
    </Box>
    <Divider />
    {Object.keys(translationFields).map((locale) => {
      const field = translationFields[locale]
      const localeErrors = translationsValidationErrors[locale]
      const nameError = localeErrors?.name
      const descriptionError = localeErrors?.description
      return (
        <Box
          key={locale}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ width: 28, fontFamily: 'monospace', flexShrink: 0 }}>
            {locale}
          </Typography>
          <TextField
            label={`${locale} name`}
            size="small"
            value={field.name}
            onChange={(e) => onUpdate(locale, 'name', e.target.value)}
            onBlur={() => onBlur?.(locale, 'name')}
            error={Boolean(nameError)}
            helperText={nameError}
            slotProps={{ formHelperText: { sx: { fontSize: '0.7rem' } } }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          {showDescription && (
            <TextField
              label={`${locale} description`}
              size="small"
              multiline
              minRows={1}
              maxRows={8}
              value={field.description}
              onChange={(e) => onUpdate(locale, 'description', e.target.value)}
              onBlur={() => onBlur?.(locale, 'description')}
              error={Boolean(descriptionError)}
              helperText={descriptionError}
              slotProps={{
                formHelperText: { sx: { fontSize: '0.7rem' } },
                htmlInput: { style: { wordBreak: 'break-word', overflowWrap: 'break-word' } },
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  height: 'auto',
                  '& .MuiOutlinedInput-input': { padding: '6px 12.5px' },
                },
              }}
            />
          )}
        </Box>
      )
    })}
  </Box>
)
